import axios from "axios";
import { useEffect, useRef, useState } from "react";
import {
  FiBriefcase,
  FiCalendar,
  FiDownload,
  FiEye,
  FiFilter,
  FiSearch,
  FiStar,
  FiX,
  FiUser,
  FiUsers,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiClipboard,
  FiTrash2,
  FiMail,
  FiPhone,
  FiMapPin,
  FiAward,
  FiCheck,
  FiAlertCircle,
  FiExternalLink,
} from "react-icons/fi";
import { FaCalendarAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../utils/config";
import { formatSecondsToTime, getAssessmentTimeTaken } from "../utils/assessmentTime";
import "../index.css";
import "./EmployeeDashboard.css";
import "./EmployeeLeaves.css";

const JobApplicants = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [scoreFilter, setScoreFilter] = useState(0);
  const [roleFilter, setRoleFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ✅ Server-side pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
  });

  // ✅ Backend stats
  const [backendStats, setBackendStats] = useState({
    totalApplications: 0,
    totalSelected: 0,
    totalInterview: 0,
    avgTechnicalScore: 0,
  });

  const roleDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  const clientId = localStorage.getItem("clientId");

  // ✅ Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ✅ Filters change hone pe page 1 se fetch
  useEffect(() => {
    if (clientId) fetchApplications(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, roleFilter, statusFilter, scoreFilter, dateFilter]);

  // ✅ Page change hone pe fetch
  useEffect(() => {
    if (clientId) fetchApplications(pagination.currentPage, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.limit]);

  useEffect(() => {
    fetchRoles();

    const handleClickOutside = (event) => {
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target)
      ) {
        setIsRoleDropdownOpen(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setIsStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/roles/all`);
      if (res.data.success) {
        const rolesData = res.data.data || [];
        const formattedRoles = rolesData.map((role, index) => ({
          _id: role._id || index,
          name: role.roleName || role.name || role,
        }));
        const uniqueRoles = Array.from(
          new Set(formattedRoles.map((r) => r.name))
        )
          .filter(Boolean)
          .map((name) => formattedRoles.find((r) => r.name === name));
        setRoles(uniqueRoles);
      }
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    }
  };

  // ✅ Server-side pagination + filters
  const fetchApplications = async (page = 1, limit = 10) => {
    if (!clientId) return;

    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (debouncedSearch) params.append("search", debouncedSearch);
      if (roleFilter) params.append("role", roleFilter);
      if (statusFilter) params.append("status", statusFilter);
      if (scoreFilter > 0) params.append("minScore", String(scoreFilter));
      if (dateFilter) params.append("date", dateFilter);

      const res = await axios.get(
        `${API_BASE_URL}/applications/all/${clientId}?${params.toString()}`
      );

      if (res.data.success) {
        setApplications(res.data.applications || []);

        const pg = res.data.pagination || {};
        setPagination({
          currentPage: pg.currentPage || 1,
          totalPages: pg.totalPages || 1,
          totalCount: pg.totalApplications || 0,
          limit: pg.limit || limit,
        });

        if (res.data.stats) {
          setBackendStats(res.data.stats);
        }
      } else {
        setError(res.data.message || "Failed to fetch applications");
        setApplications([]);
      }
    } catch (err) {
      setError("Failed to fetch applications");
      console.error(err);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDocumentUrl = (filePath) => {
    if (!filePath) return "";
    const relativePath = filePath.includes("uploads")
      ? "uploads/" + filePath.split(/uploads[\\/]/).pop().replace(/\\/g, "/")
      : filePath.replace(/\\/g, "/");
    return `${API_BASE_URL.replace("/api", "")}/${relativePath}`;
  };

  const handleOpenModal = (app) => {
    setSelectedApplicant(app);
    setIsModalOpen(true);
  };

  const handleOpenAssessmentModal = (app) => {
    window.location.href = `/score?search=${encodeURIComponent(app.firstName || "")}`;
  };

  const handleUpdateScore = async (id, field, value) => {
    const numericValue = field === "status" ? value : Number(value);

    try {
      const res = await axios.post(`${API_BASE_URL}/applications/update-score`, {
        applicationId: id,
        [field]: numericValue,
      });
      if (res.data.success) {
        setApplications((prev) =>
          prev.map((app) =>
            app._id === id ? { ...app, [field]: numericValue } : app
          )
        );
        toast.success("Score updated successfully!");
      }
    } catch (err) {
      console.error("Update score error:", err);
      toast.error("Failed to update score.");
    }
  };

  const handleResignationStatusUpdate = async (id, status) => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${API_BASE_URL}/applications/resignation-approval`,
        {
          applicationId: id,
          status,
        }
      );
      if (res.data.success) {
        toast.success(`Resignation ${status} successfully!`);
        setApplications((prev) =>
          prev.map((app) =>
            app._id === id ? { ...app, resignationStatus: status } : app
          )
        );
        if (selectedApplicant && selectedApplicant._id === id) {
          setSelectedApplicant({
            ...selectedApplicant,
            resignationStatus: status,
          });
        }
      }
    } catch (err) {
      console.error("Resignation approval error:", err);
      toast.error("Failed to update resignation status.");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    setScoreFilter(0);
    setRoleFilter("");
    setDateFilter("");
    setStatusFilter("");
    setRoleSearchQuery("");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = Boolean(
    searchQuery || scoreFilter > 0 || roleFilter || dateFilter || statusFilter
  );

  // ✅ Server-side pagination handlers
  const handleItemsPerPageChange = (limit) => {
    setPagination((prev) => ({ ...prev, currentPage: 1, limit }));
  };

  const handlePrevPage = () => {
    if (pagination.currentPage > 1) {
      setPagination((prev) => ({ ...prev, currentPage: prev.currentPage - 1 }));
    }
  };

  const handleNextPage = () => {
    if (pagination.currentPage < pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: prev.currentPage + 1 }));
    }
  };

  const handlePageClick = (page) => {
    if (typeof page === "number") {
      setPagination((prev) => ({ ...prev, currentPage: page }));
    }
  };

  const getVisiblePages = () => {
    const pages = [];
    const total = pagination.totalPages;
    const current = pagination.currentPage;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push("...");

      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);

      for (let i = start; i <= end; i++) {
        if (i > 1 && i < total) pages.push(i);
      }

      if (current < total - 2) pages.push("...");
      if (total > 1) pages.push(total);
    }

    return pages;
  };

  const downloadCSV = () => {
    if (applications.length === 0) {
      toast.info("No applicant records to download!");
      return;
    }

    const headers = [
      "Applicant ID",
      "Candidate Name",
      "Email",
      "Mobile",
      "Applied Role",
      "Technical Score",
      "Appearance Score",
      "Work Knowledge",
      "Status",
      "Applied Date",
    ];

    const csvRows = [
      headers.join(","),
      ...applications.map((app) => {
        return [
          `"${app._id || ""}"`,
          `"${(app.firstName || "") + " " + (app.lastName || "")}"`,
          `"${app.email || ""}"`,
          `"${app.mobile || ""}"`,
          `"${(app.jobId?.role || "Applicant").replace(/"/g, '""')}"`,
          app.technicalScore || 0,
          app.appearanceScore || 0,
          app.workKnowledge || 0,
          `"${app.status || "Applied"}"`,
          `"${app.appliedAt ? new Date(app.appliedAt).toLocaleDateString("en-IN") : "-"}"`,
        ].join(",");
      }),
    ];

    const csvData = csvRows.join("\n");
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `job_applicants_${new Date()
      .toLocaleDateString("en-IN")
      .replace(/\//g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getPeriodLabel = () => {
    return new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "selected":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "rejected":
        return "bg-rose-50 text-rose-700 border-rose-200";
      case "resigned":
        return "bg-orange-50 text-orange-700 border-orange-200";
      case "pending":
        return "bg-amber-50 text-amber-700 border-amber-200";
      default:
        return "bg-indigo-50 text-indigo-700 border-indigo-200";
    }
  };

  if (!clientId) {
    return (
      <div className="emp-dash">
        <main className="grid place-items-center min-h-[60vh] p-4">
          <div className="emp-dash__card max-w-[480px] w-full p-8 text-center">
            <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-3">
              <FiUser size={24} />
            </div>
            <h3 className="text-base font-bold text-gray-900">Authentication Required</h3>
            <p className="text-xs text-gray-500 mt-1">Please login to access candidate applications.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "white" }} className="emp-dash">
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar={false} />
      <main>
        {/* Page Header */}
        <div className="emp-dash__header">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="emp-dash__greeting text-lg sm:text-xl font-bold whitespace-nowrap">
              Job <span>Applicants</span>
            </h1>
            <span className="text-xs text-slate-400 font-medium hidden sm:inline">
              Screen candidate profiles, evaluate assessment scores, and track selection.
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="emp-dash__date-pill">
              <FaCalendarAlt />
              <span>{getPeriodLabel()}</span>
            </div>
            <button
              type="button"
              onClick={downloadCSV}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
            >
              <FiDownload size={14} /> Export CSV
            </button>
          </div>
        </div>

        {/* Top KPI Metrics — Backend stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Total Applicants</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--indigo">
                <FiUsers />
              </div>
            </div>
            <div className="emp-dash__stat-value">{backendStats.totalApplications}</div>
            <div className="emp-dash__stat-meta">all submitted applications</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Selected Hires</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--emerald">
                <FiCheckCircle />
              </div>
            </div>
            <div className="emp-dash__stat-value text-emerald-700">
              {backendStats.totalSelected}
            </div>
            <div className="emp-dash__stat-meta">candidates confirmed</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Interviews</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rose">
                <FiCalendar />
              </div>
            </div>
            <div className="emp-dash__stat-value text-rose-600">
              {backendStats.totalInterview}
            </div>
            <div className="emp-dash__stat-meta">in interview stage</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Filtered Results</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--violet">
                <FiFilter />
              </div>
            </div>
            <div className="emp-dash__stat-value text-violet-700">
              {pagination.totalCount}
            </div>
            <div className="emp-dash__stat-meta">matching active filter</div>
          </div>

          <div className="emp-dash__stat col-span-2 lg:col-span-1">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Average Score</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rate">
                <FiStar />
              </div>
            </div>
            <div className="emp-dash__stat-value text-indigo-700">
              {backendStats.avgTechnicalScore}%
            </div>
            <div className="emp-dash__stat-meta">technical test average</div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div className="emp-dash__card p-4 mb-6">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
              <div className="relative min-w-[200px] flex-1 max-w-[280px]">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input
                  type="text"
                  placeholder="Search name, phone, role, email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white font-medium"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX size={12} />
                  </button>
                )}
              </div>

              {/* Role Dropdown */}
              <div className="relative" ref={roleDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsRoleDropdownOpen((prev) => !prev)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border transition-all bg-white whitespace-nowrap cursor-pointer ${
                    roleFilter
                      ? "border-indigo-500 text-indigo-700 bg-indigo-50"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <FiBriefcase className="text-gray-400 text-xs" />
                  <span className="truncate max-w-[120px]">
                    {roleFilter || "All Roles"}
                  </span>
                  <span className="text-gray-400 text-[10px]">▾</span>
                </button>

                {isRoleDropdownOpen && (
                  <div className="absolute z-50 mt-1.5 w-56 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto p-1">
                    <div className="p-1.5 border-b border-gray-100 sticky top-0 bg-white">
                      <input
                        type="text"
                        placeholder="Search roles..."
                        value={roleSearchQuery}
                        onChange={(e) => setRoleSearchQuery(e.target.value)}
                        className="w-full py-1 px-2 text-xs bg-gray-50 border border-gray-200 rounded-lg outline-none focus:bg-white focus:border-indigo-600"
                        autoFocus
                      />
                    </div>
                    <div
                      onClick={() => {
                        setRoleFilter("");
                        setIsRoleDropdownOpen(false);
                      }}
                      className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer transition-colors ${
                        !roleFilter
                          ? "bg-indigo-50 text-indigo-700 font-bold"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      All Roles
                    </div>
                    {roles
                      .filter((r) =>
                        r.name.toLowerCase().includes(roleSearchQuery.toLowerCase())
                      )
                      .map((r) => (
                        <div
                          key={r._id}
                          onClick={() => {
                            setRoleFilter(r.name);
                            setIsRoleDropdownOpen(false);
                          }}
                          className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer transition-colors ${
                            roleFilter === r.name
                              ? "bg-indigo-50 text-indigo-700 font-bold"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {r.name}
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Status Dropdown */}
              <div className="relative" ref={statusDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsStatusDropdownOpen((prev) => !prev)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border transition-all bg-white whitespace-nowrap cursor-pointer ${
                    statusFilter
                      ? "border-indigo-500 text-indigo-700 bg-indigo-50"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <FiCheckCircle className="text-gray-400 text-xs" />
                  <span className="capitalize">{statusFilter || "All Statuses"}</span>
                  <span className="text-gray-400 text-[10px]">▾</span>
                </button>

                {isStatusDropdownOpen && (
                  <div className="absolute z-50 mt-1.5 w-44 bg-white border border-gray-200 rounded-xl shadow-xl p-1">
                    {["", "Selected", "Pending", "Rejected", "Resigned", "Applied"].map(
                      (st) => (
                        <div
                          key={st}
                          onClick={() => {
                            setStatusFilter(st);
                            setIsStatusDropdownOpen(false);
                          }}
                          className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer capitalize transition-colors ${
                            statusFilter === st
                              ? "bg-indigo-50 text-indigo-700 font-bold"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {st || "All Statuses"}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>

              {/* Score Filter */}
              <div className="relative">
                <select
                  value={scoreFilter}
                  onChange={(e) => setScoreFilter(Number(e.target.value))}
                  className="h-[34px] px-3 py-1 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white font-medium cursor-pointer"
                >
                  <option value="0">All Scores</option>
                  <option value="60">60% & Above</option>
                  <option value="70">70% & Above</option>
                  <option value="80">80% & Above</option>
                  <option value="90">90% & Above</option>
                </select>
              </div>

              {/* Date Filter */}
              <div className="relative">
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="h-[34px] px-2.5 py-1 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white font-medium cursor-pointer"
                />
              </div>
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all whitespace-nowrap cursor-pointer self-end lg:self-center"
              >
                <FiX size={13} /> Reset Filters
              </button>
            )}
          </div>
        </div>

        {/* Applicants Table */}
        <div className="emp-dash__card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                <FiUsers size={15} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Applicants Directory
                </h2>
                <p className="text-[10px] text-gray-500">
                  Showing {applications.length} of {pagination.totalCount} candidates
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="font-medium">Rows per page:</span>
              <select
                value={pagination.limit}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-xs font-semibold outline-none focus:border-indigo-600"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200/80">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50/90 border-b border-gray-200 text-gray-600 uppercase text-[10px] tracking-wider font-bold">
                  <th className="py-3.5 px-4 w-12 text-center">S.No</th>
                  <th className="py-3.5 px-4 min-w-[220px]">Candidate Details</th>
                  <th className="py-3.5 px-4 min-w-[150px]">Applied Role</th>
                  <th className="py-3.5 px-4 min-w-[140px]">Contact Info</th>
                  <th className="py-3.5 px-4 text-center min-w-[120px]">
                    Technical Score
                  </th>
                  <th className="py-3.5 px-4 text-center min-w-[130px]">Status</th>
                  <th className="py-3.5 px-4 text-center min-w-[160px]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-3" />
                        <span className="text-xs text-gray-500 font-medium">
                          Loading candidate profiles...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : applications.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-12 h-12 rounded-xl bg-gray-50 text-gray-300 flex items-center justify-center mb-2">
                          <FiUsers size={24} />
                        </div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                          No Applicants Found
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          No candidate applications matched the current filters.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  applications.map((app, index) => {
                    const fullName = `${app.firstName || ""} ${app.lastName || ""}`.trim() || "Candidate";
                    const initial = (app.firstName?.charAt(0) || "C").toUpperCase();
                    const score = app.technicalScore || 0;

                    return (
                      <tr
                        key={app._id}
                        className="hover:bg-indigo-50/20 transition-colors group"
                      >
                        <td className="py-3.5 px-4 text-center text-gray-400 font-medium text-[11px]">
                          {(pagination.currentPage - 1) * pagination.limit + index + 1}
                        </td>

                        <td className="py-3.5 px-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-indigo-600 to-violet-600 text-white flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5 shadow-xs">
                              {initial}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-gray-900 text-xs truncate">
                                {fullName}
                              </p>
                              <div className="flex items-center gap-1.5 text-[10px] text-gray-500 mt-0.5">
                                <span>{app.email || "No email"}</span>
                                <span>•</span>
                                <span>
                                  {app.appliedAt
                                    ? new Date(app.appliedAt).toLocaleDateString("en-IN", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })
                                    : "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="py-3.5 px-4">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                            <FiBriefcase size={11} />
                            <span className="truncate max-w-[130px]">
                              {app.jobId?.role || app.role || "Specialist"}
                            </span>
                          </span>
                        </td>

                        <td className="py-3.5 px-4 text-gray-600 text-[11px]">
                          <div className="font-medium">{app.mobile || "N/A"}</div>
                          <span className="text-[10px] text-gray-400">
                            {app.currentLocation || "Location not set"}
                          </span>
                        </td>

                        <td className="py-3.5 px-4 text-center">
                          <div className="inline-flex items-center gap-1">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              className="w-14 px-1.5 py-1 border border-gray-200 rounded-lg text-xs font-bold text-center focus:border-indigo-600 focus:bg-white outline-none transition-colors"
                              value={app.technicalScore ?? 0}
                              onChange={(e) =>
                                handleUpdateScore(
                                  app._id,
                                  "technicalScore",
                                  e.target.value
                                )
                              }
                            />
                            <span
                              className={`text-[10px] font-extrabold ${
                                score >= 80
                                  ? "text-emerald-600"
                                  : score >= 50
                                  ? "text-amber-600"
                                  : "text-rose-600"
                              }`}
                            >
                              %
                            </span>
                          </div>
                        </td>

                        <td className="py-3.5 px-4 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${getStatusBadge(
                                app.status
                              )}`}
                            >
                              {app.status || "Applied"}
                            </span>
                            {app.status === "Resigned" &&
                              app.resignationStatus === "Pending" && (
                                <span className="text-[8px] font-extrabold text-rose-600 animate-pulse uppercase tracking-tighter">
                                  Resignation Pending
                                </span>
                              )}
                            {(app.interviewStatus === "Invited" ||
                              app.interviewStatus === "Rescheduled") &&
                              app.candidateInterviewStatus &&
                              app.candidateInterviewStatus !== "Pending" && (
                                <span
                                  className={`text-[8px] font-bold uppercase tracking-tighter ${
                                    app.candidateInterviewStatus === "Confirmed"
                                      ? "text-indigo-600"
                                      : "text-rose-600"
                                  }`}
                                >
                                  Interview {app.candidateInterviewStatus}
                                </span>
                              )}
                          </div>
                        </td>

                        <td className="py-3.5 px-4 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleOpenModal(app)}
                              className="px-2.5 py-1.5 bg-indigo-50 hover:bg-indigo-600 hover:text-white text-indigo-700 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all flex items-center gap-1 cursor-pointer"
                              title="View Full Profile"
                            >
                              <FiEye size={12} /> View
                            </button>

                            <button
                              type="button"
                              onClick={() => handleOpenAssessmentModal(app)}
                              className="px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all shadow-xs flex items-center gap-1 cursor-pointer"
                              title="Evaluate Quiz & Score"
                            >
                              <FiStar size={12} /> Score
                            </button>

                            {app.resume && (
                              <button
                                type="button"
                                onClick={() =>
                                  window.open(
                                    formatDocumentUrl(app.resume),
                                    "_blank"
                                  )
                                }
                                className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                                title="Download Resume"
                              >
                                <FiDownload size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 flex-wrap gap-2">
              <span className="text-xs text-gray-500">
                Page <strong className="text-gray-900">{pagination.currentPage}</strong> of{" "}
                <strong className="text-gray-900">{pagination.totalPages}</strong>
              </span>

              <div className="flex items-center gap-1 flex-wrap">
                <button
                  type="button"
                  onClick={handlePrevPage}
                  disabled={pagination.currentPage === 1}
                  className="px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 cursor-pointer transition-colors"
                >
                  Previous
                </button>

                {getVisiblePages().map((page, index) => {
                  if (page === "...") {
                    return (
                      <span key={`ellipsis-${index}`} className="px-1 text-gray-400 text-xs font-bold">
                        …
                      </span>
                    );
                  }
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => handlePageClick(page)}
                      className={`w-8 h-8 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                        pagination.currentPage === page
                          ? "bg-indigo-600 text-white shadow-xs"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  type="button"
                  onClick={handleNextPage}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 cursor-pointer transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* CANDIDATE DETAILS MODAL */}
      {isModalOpen && selectedApplicant && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-5 bg-gradient-to-r from-indigo-50 via-white to-violet-50 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-md shadow-indigo-600/20">
                  {(selectedApplicant.firstName?.charAt(0) || "C").toUpperCase()}
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    {selectedApplicant.firstName} {selectedApplicant.lastName}
                  </h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-600">
                    {selectedApplicant.jobId?.role || selectedApplicant.role || "Applicant"} · Applied {new Date(selectedApplicant.appliedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <FiX size={16} />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Contact & Personal Details
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-[9px] font-bold text-gray-400 uppercase">Email</p>
                    <p className="text-xs font-bold text-gray-900 mt-0.5 truncate">
                      {selectedApplicant.email || "N/A"}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-[9px] font-bold text-gray-400 uppercase">Mobile</p>
                    <p className="text-xs font-bold text-gray-900 mt-0.5">
                      {selectedApplicant.mobile || "N/A"}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-[9px] font-bold text-gray-400 uppercase">Location</p>
                    <p className="text-xs font-bold text-gray-900 mt-0.5">
                      {selectedApplicant.currentLocation || "N/A"}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-[9px] font-bold text-gray-400 uppercase">Notice Period</p>
                    <p className="text-xs font-bold text-indigo-600 mt-0.5">
                      {selectedApplicant.noticePeriod || "Immediate"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50/70 rounded-xl border border-gray-100 space-y-2">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Education
                  </h4>
                  <div className="text-xs space-y-1 text-gray-600">
                    <p><strong>Degree:</strong> {selectedApplicant.highestQualification || "N/A"}</p>
                    <p><strong>Institution:</strong> {selectedApplicant.institution || "N/A"}</p>
                    <p><strong>Department:</strong> {selectedApplicant.department || "N/A"}</p>
                    <p><strong>Percentage / CGPA:</strong> {selectedApplicant.percentage ? `${selectedApplicant.percentage}%` : "N/A"}</p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50/70 rounded-xl border border-gray-100 space-y-2">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Experience & Compensation
                  </h4>
                  <div className="text-xs space-y-1 text-gray-600">
                    <p><strong>Total Experience:</strong> {selectedApplicant.experience || 0} Years</p>
                    <p><strong>Current Company:</strong> {selectedApplicant.companyName || "N/A"}</p>
                    <p><strong>Current CTC:</strong> {selectedApplicant.currentCTC || "N/A"}</p>
                    <p><strong>Expected CTC:</strong> {selectedApplicant.expectedCTC || "N/A"}</p>
                  </div>
                </div>
              </div>

              {selectedApplicant.skills && (
                <div>
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Skills & Competencies
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedApplicant.skills.split(",").map((s, i) => (
                      <span
                        key={i}
                        className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-lg border border-indigo-100"
                      >
                        {s.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Evaluated Assessment Scores
                </h3>
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div className="p-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Tech Test</p>
                    <p className="text-sm font-extrabold text-indigo-700 mt-1">
                      {selectedApplicant.technicalScore || 0}%
                    </p>
                  </div>
                  <div className="p-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Appearance</p>
                    <p className="text-sm font-extrabold text-indigo-700 mt-1">
                      {selectedApplicant.appearanceScore || 0}%
                    </p>
                  </div>
                  <div className="p-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Knowledge</p>
                    <p className="text-sm font-extrabold text-indigo-700 mt-1">
                      {selectedApplicant.workKnowledge || 0}%
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                    <p className="text-[10px] text-emerald-800 font-bold uppercase">Overall</p>
                    <p className="text-sm font-extrabold text-emerald-700 mt-1">
                      {selectedApplicant.overallRating || 0}%
                    </p>
                  </div>
                </div>
              </div>

              {selectedApplicant.assessmentResults && selectedApplicant.assessmentResults.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                    Assessment Answer Sheets
                  </h3>
                  <div className="space-y-3">
                    {selectedApplicant.assessmentResults.map((result, idx) => {
                      const assessmentName = result.quizId?.title || result.quizId?.name || `Assessment ${idx + 1}`;
                      const assessmentDuration = result.quizId?.duration || 30;
                      const timeTaken = getAssessmentTimeTaken(result, idx, selectedApplicant.assessmentResults, selectedApplicant);
                      const attemptedDate = result.attemptedAt ? new Date(result.attemptedAt).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      }) : "N/A";

                      return (
                        <div key={idx} className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="text-sm font-bold text-gray-900 mb-1">
                                {assessmentName}
                              </h4>
                              <p className="text-[10px] text-gray-500">
                                Attempted on {attemptedDate}
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg">
                                <FiAward size={12} />
                                <span className="text-sm font-extrabold">{result.score || 0}%</span>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3 mt-3">
                            <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-blue-100">
                              <FiClock className="text-blue-600" size={14} />
                              <div>
                                <p className="text-[9px] text-gray-400 font-bold uppercase">Assessment Duration</p>
                                <p className="text-xs font-bold text-gray-800">{assessmentDuration} mins</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-blue-100">
                              <FiClock className="text-indigo-600" size={14} />
                              <div>
                                <p className="text-[9px] text-gray-400 font-bold uppercase">Time Taken</p>
                                <p className="text-xs font-bold text-gray-800">{formatSecondsToTime(timeTaken)}</p>
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 pt-3 border-t border-blue-200/50">
                            <div className="flex items-center justify-between text-[10px]">
                              <span className="text-gray-500">Questions: {result.totalQuestions || 0}</span>
                              <span className={`font-bold ${result.score >= 70 ? 'text-emerald-600' : result.score >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>
                                {result.score >= 70 ? 'Excellent' : result.score >= 50 ? 'Good' : 'Needs Improvement'}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {(selectedApplicant.interviewStatus === "Invited" ||
                selectedApplicant.interviewStatus === "Rescheduled") && (
                <div className="p-4 bg-indigo-50/50 rounded-xl border border-indigo-100">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-bold text-indigo-900 uppercase">
                      Interview Scheduled
                    </h4>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-indigo-100 text-indigo-700">
                      {selectedApplicant.candidateInterviewStatus || "Pending"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600">
                    <strong>Time:</strong> {selectedApplicant.interviewTime || "To be decided"} ·{" "}
                    <strong>Mode:</strong> {selectedApplicant.interviewMode || "Online"}
                  </p>
                  {selectedApplicant.candidateInterviewNote && (
                    <p className="text-xs text-gray-500 italic mt-2 bg-white p-2.5 rounded-lg border border-indigo-100">
                      "{selectedApplicant.candidateInterviewNote}"
                    </p>
                  )}
                </div>
              )}

              {selectedApplicant.status === "Resigned" && (
                <div className="p-4 bg-rose-50 rounded-xl border border-rose-100 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-rose-800 uppercase">
                      Resignation Request
                    </h4>
                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-full uppercase bg-rose-100 text-rose-700">
                      {selectedApplicant.resignationStatus || "Pending"}
                    </span>
                  </div>
                  {selectedApplicant.resignationLetter && (
                    <p className="text-xs text-gray-700 italic bg-white p-3 rounded-lg border border-rose-100">
                      "{selectedApplicant.resignationLetter}"
                    </p>
                  )}
                  {selectedApplicant.resignationStatus === "Pending" && (
                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() =>
                          handleResignationStatusUpdate(
                            selectedApplicant._id,
                            "Approved"
                          )
                        }
                        className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase rounded-lg transition-colors cursor-pointer"
                      >
                        Approve Resignation
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleResignationStatusUpdate(
                            selectedApplicant._id,
                            "Rejected"
                          )
                        }
                        className="flex-1 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold uppercase rounded-lg transition-colors cursor-pointer"
                      >
                        Reject Resignation
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              {selectedApplicant.resume ? (
                <button
                  type="button"
                  onClick={() =>
                    window.open(
                      formatDocumentUrl(selectedApplicant.resume),
                      "_blank"
                    )
                  }
                  className="py-2 px-4 rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <FiDownload size={13} /> View Resume
                </button>
              ) : (
                <span className="text-xs text-gray-400 italic">No resume file</span>
              )}

              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="py-2 px-5 rounded-xl bg-slate-900 hover:bg-black text-white text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobApplicants;